/**
 * print-fallback-banner.component.ts
 *
 * app-print-fallback-banner
 *
 * The bar that says printing is currently being routed around dead hardware, and
 * offers the one action that ends it. Mounted once in the app shell
 * (app.component.html), below the offline banner, so it shows on every route.
 *
 * It exists because the switch it reports is per device and set by hand: nothing
 * else in the system knows the Zebra is down, so nothing else will ever turn it
 * back off. Without a standing reminder, "the printer was down on Tuesday"
 * quietly becomes how that machine prints. Hence no dismiss control — unlike the
 * offline banner, which reports a condition that resolves itself. The only way
 * to make this go away is to turn the mode off, which is the point.
 *
 * Colours come from the global theme tokens (warning amber on graphite, the
 * app's "you need to deal with this" pair) and it overrides no global rule.
 */

import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { PrintFallbackService } from '../../../service/print-fallback.service';
import { PrintFallbackMode } from '../../../domain/receiving-log/rules/print-path.rules';

@Component({
  selector: 'app-print-fallback-banner',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './print-fallback-banner.component.html',
  styleUrls: ['./print-fallback-banner.component.css'],
})
export class PrintFallbackBannerComponent implements OnInit, OnDestroy {
  private readonly _printFallback = inject(PrintFallbackService);
  private _modeSubscription?: Subscription;

  /** The active mode; 'normal' hides the banner. */
  public mode: PrintFallbackMode = 'normal';

  public get isVisible(): boolean { return this.mode !== 'normal'; }

  /**
   * What is happening to the printing — the headline half of the message.
   *
   * Says PRINTING WAS REROUTED, not "the print system is down". Only a person
   * turns this on, so the banner reports their action, not a diagnosis: the same
   * words used to appear at a facility whose printing was configured this way
   * from the start, where nothing was down and nobody had chosen anything.
   *
   * Worded to match the Printing screen exactly — "printed from a desktop
   * computer only", never "by hand". Someone flips a switch there and reads this
   * a second later; two names for one state reads as two different states.
   */
  public get title(): string {
    switch (this.mode) {
      case 'printApiDown':
        return 'Labels and copier sheets print from a desktop computer only';
      case 'copierDown':
        return 'Copier sheets print from a desktop computer only';
      default:
        return 'Labels print from a desktop computer only';
    }
  }

  /**
   * What that means for the operator's printing right now.
   *
   * "SET ON THIS DEVICE", not "someone reported it". Nothing is reported
   * anywhere: this is a browser-storage setting on one device, and it changes
   * nothing for anyone else in the building. The old wording described a
   * report that reached other people, which would have someone at the second
   * device assume it was already handled for them.
   *
   * The reassurance in the printApiDown case is load-bearing: the ReceivingMgr
   * API is a separate service from the Print API, and someone who reads "print
   * system down" as "stop receiving" costs the warehouse a shift of records.
   */
  public get detail(): string {
    switch (this.mode) {
      case 'printApiDown':
        return 'Set on this device. Both documents have to be printed from a desktop computer. '
          + 'Receiving logs still save normally.';
      case 'copierDown':
        return 'Set on this device. Copier sheets have to be printed from a desktop computer; '
          + 'labels still go to the Zebra label printer.';
      default:
        return 'Set on this device. Labels have to be printed from a desktop computer; copier '
          + 'sheets still print normally.';
    }
  }

  public ngOnInit(): void {
    this._modeSubscription = this._printFallback.mode$.subscribe((mode) => (this.mode = mode));
  }

  public ngOnDestroy(): void {
    this._modeSubscription?.unsubscribe();
  }

  /** Back to normal printing, everywhere, with no reload. */
  public onTurnOff(): void {
    this._printFallback.clear();
  }
}
